


import { useState, useContext } from "react";
import MainLayouts from "../../../Layout/MainLayouts";
import { ReactComponent as ChevronRightIcon } from "bootstrap-icons/icons/chevron-right.svg";
import { ReactComponent as Download } from "bootstrap-icons/icons/download.svg";
import { ReactComponent as Search } from "bootstrap-icons/icons/search.svg";
import { ReactComponent as ArrowClockwise } from "bootstrap-icons/icons/arrow-clockwise.svg";
import { ReactComponent as XCircle } from "bootstrap-icons/icons/x-circle.svg";
import Spinner from "../../spinner/Spinner";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import DataTable from "react-data-table-component";
import Swal from "sweetalert2";
import CustomStyles from "../../../components/utils/CustomStyles";
import CurrencyFormatter from "../../../components/utils/CurrencyFormatter";
import { handleError } from "../../../components/utils/AuthenticationCheck";
import AxiosApiCall from "../../../api/AxiosApiCall";
import { AuthContext } from "../../../AuthProvider/AuthProvider";
import ApiServices from "../../../api/ApiServices";
import { downloadCSVFromApi } from "../../../components/utils/DownlaodCSVFromApi";
import { useBranchStockMonitoring, useBranches, useBranchProducts } from "../../../hooks/useAppData";
import SearchableSelect from "../../../components/SearchableSelect";

const BranchStockMonitoring = () => {
    const [loadingCSV, setLoadingCSV] = useState(false);
    const [filterText, setFilterText] = useState("");
    const [resetPaginationToggle, setResetPaginationToggle] = useState(false);
    const { permissions } = useContext(AuthContext);
    const [isFilterVisible, setIsFilterVisible] = useState(false);
    const [filterLoading, setFilterLoading] = useState(false);
    const [clearFilterLoading, setClearFilterLoading] = useState(false);

    const toDateInputValue = (date) => date.toISOString().split("T")[0];

    const getDefaultDateRange = () => {
        const today = new Date();
        const oneMonthAgo = new Date(today);
        oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);
        // Handle month-end overflow (e.g. Mar 31 -> Feb 31 rolls to Mar 2/3)
        if (oneMonthAgo.getDate() !== today.getDate()) {
            oneMonthAgo.setDate(0);
        }
        return {
            start_date: toDateInputValue(oneMonthAgo),
            end_date: toDateInputValue(today),
        };
    };

    const defaultFilters = {
        per_page: 20,
        page: 1,
        branch_id: "",
        product_id: "",
        ...getDefaultDateRange(),
    };

    const [filters, setFilters] = useState(defaultFilters);
    // Dates are edited here first and only committed into `filters` (which drives
    // the API call) when the user clicks Apply Filters — so typing/adjusting a
    // date never fires a request by itself.
    const [dateDraft, setDateDraft] = useState({
        start_date: defaultFilters.start_date,
        end_date: defaultFilters.end_date,
    });

    const navigate = useNavigate();
    const axiosInst = AxiosApiCall();

    const { data: branchesResponse } = useBranches();
    const branchOpe = (branchesResponse?.data || []).slice().sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

    const { data: branchProductsResponse } = useBranchProducts("");
    const rawBranchPro = branchProductsResponse?.data?.data || branchProductsResponse?.data || [];
    const branchPro = Array.isArray(rawBranchPro) ? rawBranchPro.filter((p, i, arr) => arr.findIndex(x => x.id === p.id) === i) : [];

    const stockFilters = {
        ...filters,
        branch_id: filters.branch_id ? Number(filters.branch_id) : "",
        product_id: filters.product_id ? Number(filters.product_id) : "",
    };
    const { data: stockResponse, isLoading: loading } = useBranchStockMonitoring(stockFilters);
    const branchStock = Array.isArray(stockResponse?.data?.products)
        ? stockResponse.data.products
        : [];
    const summary = {
        totalQtySold: stockResponse?.data?.totals?.total_qty_sold || 0,
        totalQtyBought: stockResponse?.data?.totals?.total_qty_bought || 0,
        totalAmountSold: stockResponse?.data?.totals?.total_amount_sold || 0,
        totalAmountBought: stockResponse?.data?.totals?.total_amount_bought || 0,
    };

    // Check if any filter is applied (excluding default values)
    const isAnyFilterFilled = () => {
        const { per_page, page, ...filterFields } = filters;
        return Object.values(filterFields).some(
            (value) => value !== "" && value !== null
        );
    };

    // Clear all filters including dates and reset to default
    const handleClearFilters = () => {
        setClearFilterLoading(true);
        setFilterText("");
        setFilters(defaultFilters);
        setDateDraft({ start_date: defaultFilters.start_date, end_date: defaultFilters.end_date });
        toast.success("All filters cleared");
        setClearFilterLoading(false);
    };

    // Clear only date filters
    const handleClearDates = () => {
        setFilters(prev => ({
            ...prev,
            start_date: "",
            end_date: ""
        }));
        setDateDraft({ start_date: "", end_date: "" });
        toast.success("Date filters cleared");
    };

    const handleFilterClick = () => {
        const willHaveFilters =
            filters.branch_id || filters.product_id || dateDraft.start_date || dateDraft.end_date;
        if (!willHaveFilters) {
            toast.warning("Please select at least one filter!");
            return;
        }
        setFilters((prev) => ({ ...prev, start_date: dateDraft.start_date, end_date: dateDraft.end_date }));
        // React Query auto re-fetches when filters state changes (which updates stockFilters)
    };

    // Check if both dates are selected
    const areDatesSelected = filters.start_date && filters.end_date;

    const handleStartDateChange = (value) => {
        setDateDraft((prev) => ({ ...prev, start_date: value }));
    };

    const handleEndDateChange = (value) => {
        setDateDraft((prev) => ({ ...prev, end_date: value }));
    };

    // Handle CSV Download
    const handleDownloadCSV = async () => {
        if (!areDatesSelected) {
            toast.error("Please select both start and end dates to download CSV");
            return;
        }

        const startDate = new Date(filters.start_date);
        const endDate = new Date(filters.end_date);

        if (startDate > endDate) {
            toast.error("Start date must be before end date");
            return;
        }

        setLoadingCSV(true);

        try {
            const authData = JSON.parse(localStorage.getItem("auth"));
            const token = authData?.token;
            if (!token) { toast.error("No access token found"); return; }

            const downloadPayload = {
                start_date: filters.start_date,
                end_date: filters.end_date,
                download: 1,
            };
            if (filters.branch_id) downloadPayload.branch_id = Number(filters.branch_id);
            if (filters.product_id) downloadPayload.product_id = Number(filters.product_id);

            const startDateFormatted = filters.start_date.replace(/-/g, '_');
            const endDateFormatted = filters.end_date.replace(/-/g, '_');
            const filename = `branch_stock_monitoring_${startDateFormatted}_to_${endDateFormatted}.csv`;

            await downloadCSVFromApi(
                ApiServices.branchStockMonitoring,
                downloadPayload,
                token,
                {
                    filename: filename,
                    columnsToRemove: [],
                    maxRows: 50000,
                    method: "POST",
                    useBearerToken: true
                }
            );

        } catch (error) {
            // console.error("Download error:", error);
            handleError(error, navigate);
            if (error.message && error.message.includes("CSV")) {
                toast.error("Failed to process CSV file");
            } else if (error.message && error.message.includes("network")) {
                toast.error("Network error during download");
            }
        } finally {
            setLoadingCSV(false);
        }
    };

    // Handle filter changes
    const handleFilterChange = (key, value) => {
        setFilters((prev) => ({ ...prev, [key]: value }));
    };

    // Filter data based on search text (date range is applied server-side via filters)
    const filteredItems = branchStock.filter((item) => {
        const text = filterText.toLowerCase();
        return (
            item.product_name?.toLowerCase().includes(text) ||
            item.product_category?.toLowerCase().includes(text) ||
            String(item.opening_stock ?? "").includes(filterText) ||
            String(item.closing_stock ?? "").includes(filterText) ||
            String(item.current_stock ?? "").includes(filterText) ||
            String(item.qty_bought ?? "").includes(filterText) ||
            String(item.qty_sold ?? "").includes(filterText) ||
            String(item.amount_bought ?? "").includes(filterText) ||
            String(item.amount_sold ?? "").includes(filterText)
        );
    });

    const columns = [
        {
            name: "Product",
            selector: (row) =>
                row.product_name
                    ? row.product_name.replace(/_/g, " ").toUpperCase()
                    : "NOT AVAILABLE",
            sortable: true,
            width: "220px",
        },
        {
            name: "Product Category",
            selector: (row) =>
                row.product_category
                    ? row.product_category.replace(/_/g, " ").toUpperCase()
                    : "NOT AVAILABLE",
            sortable: true,
            width: "200px",
        },
        {
            name: "Opening Stock",
            selector: (row) => row.opening_stock ?? 0,
            sortable: true,
            wrap: true,
            width: "150px",
        },
        {
            name: "Closing Stock",
            selector: (row) => row.closing_stock ?? 0,
            sortable: true,
            wrap: true,
            width: "150px",
        },
        {
            name: "Current Stock",
            selector: (row) => row.current_stock ?? 0,
            sortable: true,
            wrap: true,
            width: "150px",
        },
        {
            name: "Qty Bought",
            selector: (row) => row.qty_bought ?? 0,
            sortable: true,
            width: "130px",
        },
        {
            name: "Qty Sold",
            selector: (row) => row.qty_sold ?? 0,
            sortable: true,
            width: "130px",
        },
        {
            name: "Amount Bought",
            selector: (row) => CurrencyFormatter(row.amount_bought || 0),
            sortable: true,
            width: "180px",
        },
        {
            name: "Amount Sold",
            selector: (row) => CurrencyFormatter(row.amount_sold || 0),
            sortable: true,
            width: "180px",
        },
    ];

    return (
        <MainLayouts>
            <div className="relative overflow-x-auto sm:rounded-lg py-6 w-full">
                <section
                    className="w-full pr-[3%] pt-3 my-3"
                    style={{ backgroundColor: "#F7F7F7" }}
                >
                    <nav className="bg-gray-200 py-0 px-4 rounded-lg">
                        <ol className="list-none p-0 inline-flex mb-2">
                            <li className="flex items-center">
                                <p className="text-blue-500 hover:text-blue-600 text-darkGreen3 pr-2">
                                    Operations
                                </p>
                                <ChevronRightIcon />
                            </li>
                            <li className="flex items-center">
                                <p className="text-blue-500 hover:text-blue-600 text-darkGreen3 pr-2">
                                    Stock
                                </p>
                                <ChevronRightIcon />
                            </li>
                            <li className="flex items-center">
                                <p className="text-orange1">Monitoring</p>
                            </li>
                        </ol>
                    </nav>
                </section>

                {/* Filter Controls Section */}
                <div className="pb-4 bg-white dark:bg-gray-900">
                    {/* DESKTOP FILTERS */}
                    <div className="hidden md:flex flex-wrap items-center gap-3">
                        {/* Search Input */}
                        <div className="relative flex-grow min-w-[200px]">
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                                <input
                                    type="search"
                                    className="block w-full pl-10 pr-3 py-2 text-md text-gray-900 border border-gray-300 rounded-lg bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white"
                                    placeholder="Search ..."
                                    value={filterText}
                                    onChange={(e) => setFilterText(e.target.value)}
                                />
                            </div>
                        </div>

                        {/* Clear Dates Button */}
                        {areDatesSelected && (
                            <div className="min-w-[120px]">
                                <button
                                    onClick={handleClearDates}
                                    className="w-full inline-flex justify-center items-center gap-x-2 px-4 py-2.5 text-sm font-medium text-lGrey bg-black1 rounded-lg hover:bg-gray-800 transition-colors duration-200"
                                >
                                    <XCircle className="w-4 h-4" />
                                    Clear Dates
                                </button>
                            </div>
                        )}

                        {/* Download Button */}
                        <div className="min-w-[160px]">
                            <button
                                onClick={handleDownloadCSV}
                                className={`w-full inline-flex justify-center items-center gap-x-2 rounded-md px-4 py-2.5 text-sm font-semibold transition-colors duration-200 ${areDatesSelected
                                    ? "bg-orange1 text-lGrey3 hover:bg-orange-600 cursor-pointer"
                                    : "bg-gray-300 text-gray-500 cursor-not-allowed"
                                    }`}
                                disabled={!areDatesSelected || loadingCSV}
                                title={!areDatesSelected ? "Select start and end dates to enable download" : ""}
                            >
                                {loadingCSV ? (
                                    <>
                                        <ArrowClockwise className="w-4 h-4 text-white animate-spin" />
                                        Downloading...
                                    </>
                                ) : (
                                    <>
                                        <Download className="w-4 h-4" />
                                        Download CSV
                                    </>
                                )}
                            </button>
                        </div>
                    </div>

                    {/* MOBILE FILTERS */}
                    <div className="md:hidden flex flex-col gap-3">
                        <div className="w-full">
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                                <input
                                    type="search"
                                    className="block w-full pl-10 pr-3 py-3 text-md text-gray-900 border border-gray-300 rounded-lg bg-gray-50"
                                    placeholder="Search ..."
                                    value={filterText}
                                    onChange={(e) => setFilterText(e.target.value)}
                                />
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-3">
                            {areDatesSelected && (
                                <div className="col-span-2">
                                    <button
                                        onClick={handleClearDates}
                                        className="w-full inline-flex justify-center items-center gap-x-2 px-4 py-3 text-sm font-medium text-lGrey bg-black1 rounded-lg hover:bg-gray-800"
                                    >
                                        <XCircle className="w-4 h-4" />
                                        Clear Dates
                                    </button>
                                </div>
                            )}

                            <div className="col-span-2">
                                <button
                                    onClick={handleDownloadCSV}
                                    className={`w-full inline-flex justify-center items-center gap-x-2 rounded-md px-4 py-3 text-sm font-semibold ${areDatesSelected
                                        ? "bg-orange1 text-lGrey3 hover:bg-orange-600"
                                        : "bg-gray-300 text-gray-500 cursor-not-allowed"
                                        }`}
                                    disabled={!areDatesSelected || loadingCSV}
                                >
                                    {loadingCSV ? (
                                        <>
                                            <ArrowClockwise className="w-4 h-4 text-white animate-spin" />
                                            <span className="ml-2">Downloading...</span>
                                        </>
                                    ) : (
                                        <>
                                            <Download className="w-4 h-4" />
                                            <span className="ml-2">Download CSV</span>
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Help Text */}
                    <div className="hidden md:flex items-center justify-between mt-2">
                        {!areDatesSelected && (
                            <p className="text-xs text-gray-500">
                                Select start and end dates to enable download
                            </p>
                        )}
                        {areDatesSelected && filteredItems.length > 0 && (
                            <p className="text-sm text-gray-600">
                                Showing {filteredItems.length} records from {filters.start_date} to {filters.end_date}
                            </p>
                        )}
                    </div>

                    <div className="md:hidden mt-3">
                        {!areDatesSelected && (
                            <p className="text-xs text-gray-500 text-center mb-2">
                                Select start and end dates to enable download
                            </p>
                        )}
                        {areDatesSelected && filteredItems.length > 0 && (
                            <p className="text-sm text-gray-600 text-center">
                                Showing {filteredItems.length} records
                            </p>
                        )}
                    </div>
                </div>

                {/* Advanced Filters Section */}
                <div className="pb-4 bg-white dark:bg-gray-900">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-4 w-full">
                        <SearchableSelect
                            placeholder="-- Select Branch --"
                            value={filters.branch_id}
                            onChange={(value) => setFilters({ ...filters, branch_id: value })}
                            options={branchOpe.map((branch) => ({ value: branch.id, label: branch.name }))}
                        />

                        {/* Product dropdown - now available regardless of branch selection */}
                        <SearchableSelect
                            placeholder="-- Select Product --"
                            value={filters.product_id}
                            onChange={(value) => setFilters({ ...filters, product_id: value })}
                            options={branchPro.map((product) => ({
                                value: product.id,
                                label: (product.product_units_name || "").replace(/_/g, " ") || product.name || `Product ${product.id}`,
                            }))}
                        />

                        <div className="grid grid-cols-2 gap-3 sm:col-span-2 lg:col-span-2">
                            <input
                                type="date"
                                className="border rounded px-2 sm:px-3 py-2 border-lGrey w-full min-w-0"
                                value={dateDraft.start_date}
                                onChange={(e) => handleStartDateChange(e.target.value)}
                                max={dateDraft.end_date || undefined}
                            />
                            <input
                                type="date"
                                className="border rounded px-2 sm:px-3 py-2 border-lGrey w-full min-w-0"
                                value={dateDraft.end_date}
                                onChange={(e) => handleEndDateChange(e.target.value)}
                                min={dateDraft.start_date || undefined}
                            />
                        </div>

                        <div className="flex flex-wrap gap-2 sm:col-span-2 lg:col-span-4">
                            <button
                                onClick={handleFilterClick}
                                className="bg-darkGreen3 text-lGrey px-4 py-2 rounded disabled:opacity-50 w-full sm:w-auto"
                                disabled={!isAnyFilterFilled() || filterLoading}
                            >
                                {filterLoading ? "Filtering..." : "Apply Filters"}
                            </button>

                            {isAnyFilterFilled() && (
                                <button
                                    onClick={handleClearFilters}
                                    className="bg-orange1 text-lGrey px-4 py-2 rounded hover:bg-orange-600 transition-colors w-full sm:w-auto"
                                    disabled={clearFilterLoading}
                                >
                                    {clearFilterLoading ? "Clearing..." : "Clear All Filters"}
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Filter Status Indicator */}
                    {/* {isAnyFilterFilled() && (
                        <div className="mb-4 p-3 bg-blue-50 rounded border border-blue-200">
                            <div className="flex flex-wrap items-center gap-2">
                                <span className="text-sm font-medium text-blue-700">Active Filters:</span>
                                {filters.branch_id && (
                                    <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded">
                                        Branch: {branchOpe.find(b => b.id == filters.branch_id)?.name}
                                    </span>
                                )}
                                {filters.product_id && (
                                    <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded">
                                        Product: {branchPro.find(p => p.id == filters.product_id)?.product_units_name}
                                    </span>
                                )}
                                {filters.start_date && (
                                    <span className="px-2 py-1 bg-purple-100 text-purple-800 text-xs rounded">
                                        From: {filters.start_date}
                                    </span>
                                )}
                                {filters.end_date && (
                                    <span className="px-2 py-1 bg-purple-100 text-purple-800 text-xs rounded">
                                        To: {filters.end_date}
                                    </span>
                                )}
                            </div>
                        </div>
                    )} */}
                </div>

                {/* Summary Cards */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
                    <div className="p-4 bg-white rounded-lg shadow-sm border border-gray-200 min-w-0">
                        <p className="text-sm text-gray-500">Total Qty Sold</p>
                        <p className="text-xl sm:text-2xl font-bold text-darkGreen3 break-words">{summary.totalQtySold}</p>
                    </div>
                    <div className="p-4 bg-white rounded-lg shadow-sm border border-gray-200 min-w-0">
                        <p className="text-sm text-gray-500">Total Qty Bought</p>
                        <p className="text-xl sm:text-2xl font-bold text-darkGreen3 break-words">{summary.totalQtyBought}</p>
                    </div>
                    <div className="p-4 bg-white rounded-lg shadow-sm border border-gray-200 min-w-0">
                        <p className="text-sm text-gray-500">Total Amount Sold</p>
                        <p className="text-lg sm:text-xl font-bold text-darkGreen3 break-words">{CurrencyFormatter(summary.totalAmountSold)}</p>
                    </div>
                    <div className="p-4 bg-white rounded-lg shadow-sm border border-gray-200 min-w-0">
                        <p className="text-sm text-gray-500">Total Amount Bought</p>
                        <p className="text-lg sm:text-xl font-bold text-darkGreen3 break-words">{CurrencyFormatter(summary.totalAmountBought)}</p>
                    </div>
                </div>

                {loading ? (
                    <Spinner />
                ) : (
                    <DataTable
                        columns={columns}
                        data={filteredItems}
                        pagination
                        highlightOnHover
                        striped
                        customStyles={CustomStyles}
                        noDataComponent={
                            <div className="py-8 text-center text-gray-500">
                                {isAnyFilterFilled()
                                    ? "No stock records found with the selected filters"
                                    : "No branch stock records found"}
                            </div>
                        }
                    />
                )}
            </div>
        </MainLayouts>
    );
};

export default BranchStockMonitoring;
